/**
 * Utility functions for handling image URLs from different booru sites.
 *
 * Which hosts load straight in the browser and which go through the media proxy is decided by lib/media-hosts.ts
 * (each host's `direct` rule); everything else goes through the proxy.
 */

import { findMediaHost } from "./media-hosts"
import { PLAYER_SOURCES } from "./player-embed"

/** The media proxy. /api/proxy/image is its old name, kept so the CDN's cached copies stay valid (see app/api/proxy/media). */
const MEDIA_PROXY_PATH = "/api/proxy/image"
const proxied = (url: string) => `${MEDIA_PROXY_PATH}?url=${encodeURIComponent(url)}`

/**
 * The Cloudflare media Worker (scripts/cloudflare/build-media-worker.js, lib/media-worker.ts) carries video and audio files:
 * Cloudflare does not bill for the data it sends, while the same bytes streamed through the Vercel proxy are billed per GB.
 * Off unless NEXT_PUBLIC_MEDIA_WORKER_URL is set, so removing the setting puts every video back on the Vercel proxy. Pictures
 * use it only for hosts marked `workerPictures` in lib/media-hosts.ts (Hitomi); each falls back once to the Vercel proxy when it fails.
 */
export const mediaWorkerBase = (): string => (process.env.NEXT_PUBLIC_MEDIA_WORKER_URL || "").replace(/\/+$/, "")

const AUDIO_OR_VIDEO_URL = /\.(webm|mp4|mov|avi|mkv|flv|wmv|m4v|3gp|ogv|m3u8|mp3|m4a|ogg|wav)(\?|#|$)/i

/**
 * The media Worker's cleaned video player for a source whose own player page carries scam ads (JAVAPI; lib/player-embed.ts): the same
 * player without its ad code. null when the Worker is not set up or the source has no cleaned player.
 */
export const cleanPlayerUrl = (site?: string | null, postId?: string | number | null): string | null => {
  const base = mediaWorkerBase()
  const source = site ? PLAYER_SOURCES[site] : undefined
  if (!base || !site || !source || postId == null || !source.idPattern.test(String(postId))) return null
  return `${base}/embed/${site}?id=${encodeURIComponent(String(postId))}`
}

/**
 * The media Worker's address for SAVING a file (pictures included): the Worker streams it from the host with a Content-Disposition, so the
 * visitor's browser writes it straight to disk and the bytes cost us no Vercel bandwidth. null when the Worker is not set up or the host is
 * not in lib/media-hosts.ts.
 */
export const mediaWorkerDownloadUrl = (url: string, filename: string): string | null => {
  const base = mediaWorkerBase()
  if (!base || !findMediaHost(url)) return null
  return `${base}/?url=${encodeURIComponent(url)}&download=1&filename=${encodeURIComponent(filename)}`
}

/** True for an address that points at the media Worker. */
export const isMediaWorkerUrl = (src?: string | null): boolean => {
  const base = mediaWorkerBase()
  return !!src && !!base && src.startsWith(base + "/?")
}

/**
 * The real upstream address inside one of our wrapper addresses (the Vercel proxy or the media Worker); any other address comes
 * back unchanged. Used where the real file name matters (VR detection, file type, "is it still there" checks).
 */
export const unwrapMediaUrl = (src: string): string => {
  if (!src.startsWith(MEDIA_PROXY_PATH + "?") && !isMediaWorkerUrl(src)) return src
  try {
    return new URL(src, "http://x").searchParams.get("url") || src
  } catch {
    return src
  }
}

/** The Vercel-proxy address for a media Worker address, for a player to fall back to once if the Worker fails; null for any other address. */
export const mediaWorkerFallbackUrl = (src?: string | null): string | null => {
  if (!src || !isMediaWorkerUrl(src)) return null
  const original = unwrapMediaUrl(src)
  return original === src ? null : proxied(original)
}

/**
 * Whether a URL is loaded directly by the browser instead of through the proxy.
 */
export const shouldUseDirectUrl = (url: string): boolean => {
  if (isLocalOrManagedImageUrl(url)) return true
  return Boolean(findMediaHost(url)?.direct?.(url))
}

/**
 * `crossOrigin` value a <video> needs for its src. A video loaded straight from a third-party
 * CDN (instead of through our same-origin proxy) is cross-origin: without crossOrigin="anonymous"
 * the browser refuses to upload its frames into a WebGL texture (SecurityError), so the VR
 * sphere would render black. Only hosts we've verified send Access-Control-Allow-Origin belong here.
 */
// Verified to send Access-Control-Allow-Origin: *: ARFreaks, VRHump's file host, and SexVR's file hosts (dl1/dl2.sexvr.com; SexVR videos
// are opened through our /api/sexvr-resolve, which redirects straight to one of them).
const CORS_VIDEO_SOURCE = /videos\.arfreaks\.com|cdn-r2\.vrhump\.com|\/api\/sexvr-resolve|\bdl\d*\.sexvr\.com|mp4-cdn77\.xvideos-cdn\.com/
export const getVideoCrossOrigin = (src?: string | null): "anonymous" | undefined =>
  src && (CORS_VIDEO_SOURCE.test(src) || isMediaWorkerUrl(src)) ? "anonymous" : undefined

/**
 * Images uploaded by the app or generated by the browser should not be proxied.
 * Proxing these can fail because the image proxy has a strict external-host allowlist.
 */
export const isLocalOrManagedImageUrl = (url: string): boolean => {
  if (!url) return false
  if (
    url.startsWith('/') ||
    url.startsWith('blob:') ||
    url.startsWith('data:')
  ) {
    return true
  }

  try {
    const hostname = new URL(url).hostname.toLowerCase()
    return hostname === 'blob.vercel-storage.com' ||
      hostname.endsWith('.blob.vercel-storage.com') ||
      hostname.endsWith('.public.blob.vercel-storage.com')
  } catch {
    return false
  }
}

const VIDEO_URL_PATTERN = /\.(webm|mp4|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)(\?|#|$)/i

/** A picture (not a video) hosted on one of Gelbooru's own domains, which refuse requests without their referer. */
export const isGelbooruImageUrl = (url: string): boolean => {
  try {
    const hostname = new URL(url).hostname.toLowerCase()
    const onGelbooru = hostname === "gelbooru.com" || hostname.endsWith(".gelbooru.com")
    return onGelbooru && !VIDEO_URL_PATTERN.test(url)
  } catch {
    return false
  }
}

/**
 * Get the appropriate URL for loading an image, either direct or through proxy
 */
export const getImageUrl = (url?: string | null, options?: { isFullSize?: boolean; postId?: string; site?: string; bearerToken?: string }): string => {
  if (!url) return ""

  // Don't proxy data URLs (base64 encoded images, SVGs, etc.)
  if (url.startsWith('data:')) {
    return url
  }

  // A protocol-relative address ("//host/path", which IdolSankaku returns) is not a valid URL for the proxy or for a server fetch.
  if (url.startsWith('//')) url = 'https:' + url

  if (shouldUseDirectUrl(url)) {
    return url
  }

  // Sankaku needs the viewer's login token, which the proxy forwards.
  const bearerToken = options?.bearerToken
  if (bearerToken && findMediaHost(url)?.name === "sankaku") {
    return `${proxied(url)}&token=${encodeURIComponent(bearerToken)}`
  }

  const workerBase = mediaWorkerBase()
  const host = findMediaHost(url)
  if (workerBase && host && (AUDIO_OR_VIDEO_URL.test(url) || host.workerPictures)) {
    return `${workerBase}/?url=${encodeURIComponent(url)}`
  }

  return proxied(url)
}

/**
 * The pictures a grid card can fall back to, in order, when its thumbnail fails to load: the thumbnail it was given,
 * then the post's sample, full file and preview (pictures only; a video file cannot stand in for a thumbnail). A picture
 * served by the media Worker is followed by the same picture through the Vercel proxy.
 */
export const getThumbnailFallbacks = (
  firstSrc: string,
  post: { previewUrl?: string; sampleUrl?: string; fileUrl?: string },
  bearerToken?: string,
): string[] => {
  const others = [post.sampleUrl, post.fileUrl, post.previewUrl]
    .filter((url): url is string => !!url && !VIDEO_URL_PATTERN.test(url))
    .map((url) => getImageUrl(url, { bearerToken }))
  const withProxyFallbacks = [firstSrc, ...others].flatMap((src) => [src, mediaWorkerFallbackUrl(src)])
  return Array.from(new Set(withProxyFallbacks.filter((src): src is string => !!src)))
}

/**
 * Get the best available image URL from a booru post, with proxy handling
 */
export const getBestImageUrl = (
  post: { previewUrl?: string; sampleUrl?: string; fileUrl?: string; site?: string }, 
  bearerToken?: string,
  quality?: "preview" | "sample" | "full"
): string => {
  // Determine which URL to use based on quality preference
  let url: string | undefined
  
  if (quality === "full") {
    url = post.fileUrl || post.sampleUrl || post.previewUrl
  } else if (quality === "sample") {
    url = post.sampleUrl || post.fileUrl || post.previewUrl
  } else {
    // preview or default
    url = post.previewUrl || post.sampleUrl || post.fileUrl
  }
  
  const result = getImageUrl(url, { site: post.site, bearerToken }) || "/placeholder.svg"
  
  // Debug logging for Gelbooru specifically
  if (url && url.includes('gelbooru.com')) {
    console.log('[v0] Gelbooru image URL processing:', {
      originalUrl: url,
      quality,
      shouldUseDirect: shouldUseDirectUrl(url),
      finalUrl: result,
      post: { previewUrl: post.previewUrl, sampleUrl: post.sampleUrl, fileUrl: post.fileUrl }
    })
  }
  
  // Debug logging for Sankaku specifically
  if (url && url.includes('sankakucomplex.com')) {
    console.log('[v0] Sankaku image URL processing:', {
      originalUrl: url,
      quality,
      shouldUseDirect: shouldUseDirectUrl(url),
      finalUrl: result,
      hasBearerToken: !!bearerToken,
      post: { previewUrl: post.previewUrl, sampleUrl: post.sampleUrl, fileUrl: post.fileUrl }
    })
  }
  
  return result
}
